import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Shell = ({ children, testId }) => (
  <div className="grain flex min-h-screen items-center justify-center px-6" data-testid={testId}>
    <div className="relative z-10 w-full max-w-sm">{children}</div>
  </div>
);

const Cta = ({ to, children, testId }) => (
  <Link to={to} data-testid={testId} className="mt-8 flex h-14 items-center justify-center rounded-xl bg-slate-900 text-lg font-semibold text-white">{children}</Link>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking");

  useEffect(() => {
    const id = params.get("session_id");
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${id}`);
        if (data.payment_status === "paid") { await refreshUser(); setState("paid"); return; }
        if (data.status === "expired") { setState("failed"); return; }
      } catch { /* retry */ }
      if (++tries < 15) timer = setTimeout(poll, 2000); else setState("timeout");
    };
    if (id) poll(); else setState("failed");
    return () => clearTimeout(timer);
  }, [params, refreshUser]);

  if (state === "checking") return <Shell testId="payment-checking"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /><p className="mt-4 text-slate-600">Confirming your payment...</p></Shell>;
  if (state === "paid") return (
    <Shell testId="payment-success">
      <CheckCircle2 className="h-12 w-12 text-emerald-600" />
      <h1 className="mt-4 text-3xl font-extrabold tracking-tight">You're all set</h1>
      <p className="mt-2 text-slate-600">Your subscription is active and your quote link is live.</p>
      <Cta to="/dashboard" testId="payment-success-dashboard-link">Go to my leads</Cta>
    </Shell>
  );
  return (
    <Shell testId="payment-pending">
      <h1 className="text-3xl font-extrabold tracking-tight">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
      <p className="mt-2 text-slate-600">{state === "timeout" ? "This can take a minute. Refresh your dashboard shortly." : "No charge was made. You can try again anytime."}</p>
      <Cta to="/dashboard" testId="payment-pending-dashboard-link">Back to dashboard</Cta>
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell testId="payment-cancel">
      <XCircle className="h-12 w-12 text-slate-400" />
      <h1 className="mt-4 text-3xl font-extrabold tracking-tight">Checkout canceled</h1>
      <p className="mt-2 text-slate-600">No charge was made.</p>
      <Cta to="/dashboard" testId="payment-cancel-dashboard-link">Back to dashboard</Cta>
    </Shell>
  );
}
